import { FieldTypes, Field, OfficeRecord } from "@types";
import {
  findBrandLocationOfficeRecord,
  getOfficeLocationOptionValue,
  officeLocationSlugMatches,
} from "utils/officeLocation";

export const AIRTABLE_GENERAL_FORM = "General v2";

type OfficeAddress = {
  postcode?: string;
  postalCode?: string;
};

type OfficeOption = {
  label: string;
  value: string;
  postcode?: string;
  officeSlug: string;
  locationSlug?: string;
};

const getAddressPostcode = (address: unknown): string | undefined => {
  const addr = address as OfficeAddress | undefined;
  return addr?.postcode || addr?.postalCode || undefined;
};

const hasOfficeChildren = (office: OfficeRecord): boolean => (office.children?.length ?? 0) > 0;

const isMultiLocationOfficeGroupWithChildren = (office: OfficeRecord): boolean =>
  hasOfficeChildren(office) && (office.officeLocations?.length ?? 0) > 1;

const getOfficeLocationOptions = (office: OfficeRecord): OfficeOption[] => {
  if (isMultiLocationOfficeGroupWithChildren(office)) {
    return [];
  }

  const locations = office.officeLocations ?? [];
  const locationCount = locations.length;

  if (locationCount <= 1) {
    const postcode = getAddressPostcode(locations[0]?.officeAddress);
    return [
      {
        label: postcode ? `${postcode} - ${office.title}` : office.title,
        value: office.slug,
        postcode,
        officeSlug: office.slug,
        locationSlug: locations[0]?.slug,
      },
    ];
  }

  return locations.map((location) => {
    const postcode = getAddressPostcode(location.officeAddress);
    return {
      label: postcode ? `${postcode} - ${location.title}` : location.title,
      value: getOfficeLocationOptionValue(office, location),
      postcode,
      officeSlug: office.slug,
      locationSlug: location.slug,
    };
  });
};

const getParentOfficesWithChildrenOptions = (offices: OfficeRecord[]): OfficeOption[] =>
  offices
    .filter(isMultiLocationOfficeGroupWithChildren)
    .map((office) => ({
      label: office.title,
      value: office.slug,
      officeSlug: office.slug,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const sortOfficeOptions = (options: OfficeOption[]): OfficeOption[] =>
  [...options].sort((a, b) => {
    const hasPostcodeA = Boolean(a.postcode);
    const hasPostcodeB = Boolean(b.postcode);

    if (hasPostcodeA && !hasPostcodeB) return -1;
    if (!hasPostcodeA && hasPostcodeB) return 1;

    if (a.postcode && b.postcode) {
      const postcodeComparison = a.postcode.localeCompare(b.postcode);
      if (postcodeComparison !== 0) {
        return postcodeComparison;
      }
    }

    return a.label.localeCompare(b.label);
  });

export const getOfficeOptionsMeta = (offices?: OfficeRecord[]): OfficeOption[] | undefined => {
  if (!offices?.length) {
    return undefined;
  }

  const options = offices.flatMap(getOfficeLocationOptions);
  const seen = new Set<string>();
  const deduped = options.filter((option) => {
    if (seen.has(option.value)) {
      return false;
    }
    seen.add(option.value);
    return true;
  });

  const sortedLocationOptions = sortOfficeOptions(deduped);
  const parentOfficesWithChildren = getParentOfficesWithChildrenOptions(offices);

  return [...sortedLocationOptions, ...parentOfficesWithChildren];
};

export const getOfficeOptions = (offices?: OfficeRecord[]) =>
  getOfficeOptionsMeta(offices)?.map(({ label, value }) => ({ label, value }));

/** Match query params against dropdown option metadata; `office` slug scopes the search. */
export const matchOfficeOptionByQueryParams = (
  options: OfficeOption[] | undefined,
  officeSlug: string | null,
  locationSlug: string | null,
  offices?: OfficeRecord[],
): OfficeOption | null => {
  if (!options?.length) {
    return null;
  }

  const candidates = officeSlug
    ? options.filter((option) => option.officeSlug === officeSlug)
    : options;

  if (officeSlug && !candidates.length) {
    return null;
  }

  if (locationSlug) {
    const matched = candidates.find(
      (option) =>
        option.locationSlug && officeLocationSlugMatches(option.locationSlug, locationSlug),
    );
    if (matched) {
      return matched;
    }

    if (officeSlug && offices?.length) {
      const locationOffice = findBrandLocationOfficeRecord(offices, officeSlug, locationSlug);
      const locationOfficeOption = locationOffice
        ? options.find((option) => option.value === locationOffice.slug)
        : undefined;
      if (locationOfficeOption) {
        return locationOfficeOption;
      }
    }

    if (officeSlug) {
      return candidates.find((option) => option.value === officeSlug) ?? null;
    }
    return null;
  }

  if (officeSlug) {
    return candidates.find((option) => option.value === officeSlug) ?? null;
  }

  return null;
};

/** Converts internal dropdown values to Airtable-friendly office/location slugs. */
export const transformGeneralFormValuesForAirtable = (
  values: Record<string, unknown>,
  offices?: OfficeRecord[],
): Record<string, unknown> => {
  if (!offices?.length || typeof values.selected_office !== "string") {
    return values;
  }

  const option = getOfficeOptionsMeta(offices)?.find(
    (entry) => entry.value === values.selected_office,
  );

  if (!option) {
    return values;
  }

  return {
    ...values,
    selected_office: option.officeSlug,
    office_location: option.locationSlug ?? values.office_location ?? null,
  };
};

export const getContactFormFields = (
  offices?: OfficeRecord[],
  t: (key: string) => string = (str: string) => str,
): Field[] => {
  const field = (
    id: string,
    type: FieldTypes,
    required = false,
    options: any[] | undefined = undefined,
  ) => ({
    id,
    name: id,
    label: type === FieldTypes.hidden ? "hidden" : t(`form.${id}`),
    type,
    required,
    ...(options && { options }),
  });

  return [
    field("firstname", FieldTypes.text, true),
    field("lastname", FieldTypes.text, true),
    field("email", FieldTypes.email, true),
    field("phone", FieldTypes.text, true),
    field("subject", FieldTypes.text, true),
    field("message", FieldTypes.textArea, true),
    field("company", FieldTypes.text),
    field("has_vat_number", FieldTypes.singleCheckbox),
    { ...field("company_number", FieldTypes.text, true), conditional: "has_vat_number:true" },
    field("legal_form", FieldTypes.select, true, [
      { value: "sole proprietorship", label: t("form.options.legal_form.sole_proprietorship") },
      { value: "corporation", label: t("form.options.legal_form.corporation") },
      { value: "simple partnership", label: t("form.options.legal_form.simple_partnership") },
      { value: "association", label: t("form.options.legal_form.association") },
      { value: "i don't know", label: t("form.options.legal_form.i_dont_know") },
    ]),
    field("place_of_business", FieldTypes.text, true),
    field("selected_office", FieldTypes.select, false, getOfficeOptions(offices)),
    field("gdpr_consent", FieldTypes.singleCheckbox, true),

    field("locale", FieldTypes.hidden),
    field("web_referral", FieldTypes.hidden),
    field("office_location", FieldTypes.hidden),
    ...["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].map((id) =>
      field(id, FieldTypes.hidden),
    ),
  ];
};
